/**
 * The Records list's last filter set, kept for the session (#875).
 *
 * The filters live in component state and went with the page when the reader
 * left for another section. The last set is written here and put back when the
 * page opens with no filter of its own in the address (the sidebar, Back to a
 * bare `/records`); a link that carries filters replaces the saved set whole.
 *
 * Per user AND organization: the key holds both, so the next account on the
 * same browser and the other organization of the same account never find this
 * one's project or agent. `sessionStorage` — a remembered filter is for the
 * working session, not for the next morning's «where are my calls?».
 *
 * Every access is guarded: storage may be absent, full, or disabled (private
 * mode, embedded frames) and the page then simply does not remember. What is
 * read back is validated field by field — a stale or hand-edited entry falls
 * back to the default for that field instead of breaking the list's request.
 */
import { RECORD_OUTCOME_FILTERS, listRecordsQuerySchema } from '@aica/api-types';
import { CALL_CATEGORY_OPTIONS } from '@/lib/records/categories';
import { parseScriptStepFilter } from '@/lib/records/script-step-filter';
import type {
  AgentRatingMode,
  CrmEntityTypeFilter,
  DirectionFilter,
  SentimentFilter,
  StatusFilter,
} from './useRecordsFilters';
import type { CallCategory } from '@aica/api-types';

/** The filters a reader sets by hand and gets back; see `useRecordsFilters`. */
export interface PersistedRecordsFilters {
  channelFilter: 'all' | 'call' | 'chat';
  directionFilter: DirectionFilter;
  categoryFilter: 'all' | CallCategory;
  projectFilter: string;
  agentFilter: string;
  crmEntityTypeFilter: CrmEntityTypeFilter;
  statusFilter: StatusFilter;
  participantFilter: string;
  activityFilter: string;
  dateFrom: string;
  dateTo: string;
  /** The reader's switch; the list may override it (see `useRecordsFilters`). */
  hideShortCalls: boolean;
  agentRatingFilter: number;
  agentRatingMode: AgentRatingMode;
  qaScoreMin: number;
  qaScoreMax: number;
  sentimentFilter: SentimentFilter;
  /** «Шаг скрипта не выполнен» (#879): `<stepId>:missed`, пусто — фильтра нет. */
  scriptStepFilter: string;
}

export const DEFAULT_PERSISTED_RECORDS_FILTERS: PersistedRecordsFilters = {
  channelFilter: 'all',
  directionFilter: 'all',
  categoryFilter: 'all',
  projectFilter: 'all',
  agentFilter: 'all',
  crmEntityTypeFilter: 'all',
  statusFilter: 'all',
  participantFilter: '',
  activityFilter: '',
  dateFrom: '',
  dateTo: '',
  hideShortCalls: false,
  agentRatingFilter: 0,
  agentRatingMode: 'gte',
  qaScoreMin: 0,
  qaScoreMax: 100,
  sentimentFilter: 'all',
  scriptStepFilter: '',
};

const STORAGE_PREFIX = 'aica:records:filters:v1';
const MAX_TEXT = 200;

/** Null while the user or the organization is not known yet: nothing to key on. */
export function recordsFiltersStorageKey(
  userId: string | null | undefined,
  orgId: string | null | undefined
): string | null {
  return userId && orgId ? `${STORAGE_PREFIX}:${userId}:${orgId}` : null;
}

type FieldReader<T> = (value: unknown, fallback: T) => T;

const oneOf =
  <T extends string>(allowed: readonly T[]): FieldReader<T> =>
  (value, fallback) =>
    typeof value === 'string' && (allowed as readonly string[]).includes(value)
      ? (value as T)
      : fallback;

const text: FieldReader<string> = (value, fallback) =>
  typeof value === 'string' && value.length <= MAX_TEXT ? value : fallback;

/**
 * The list API takes `projectId` only as the server's own rule reads it
 * (listRecordsQuerySchema); a stale or hand-edited saved value would otherwise
 * send a 400 on every visit until the filter is cleared by hand (Codex on
 * #1758). The schema is the rule itself, so the two cannot drift.
 */
const projectId: FieldReader<string> = (value, fallback) =>
  value === 'all' ||
  (typeof value === 'string' &&
    listRecordsQuerySchema.shape.projectId.safeParse(value).success)
    ? value
    : fallback;

const day: FieldReader<string> = (value, fallback) =>
  value === '' || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value))
    ? value
    : fallback;

const flag: FieldReader<boolean> = (value, fallback) =>
  typeof value === 'boolean' ? value : fallback;

/**
 * The saved step filter is judged by the server's own rule: a string it would
 * refuse is not kept, or the list would answer 400 on every visit.
 */
const scriptStep: FieldReader<string> = (value, fallback) =>
  value === '' || parseScriptStepFilter(value) ? (value as string) : fallback;

const integer =
  (min: number, max: number): FieldReader<number> =>
  (value, fallback) =>
    typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max
      ? value
      : fallback;

const READERS: {
  [K in keyof PersistedRecordsFilters]: FieldReader<PersistedRecordsFilters[K]>;
} = {
  channelFilter: oneOf(['all', 'call', 'chat'] as const),
  directionFilter: oneOf(['all', 'inbound', 'outbound', 'unknown'] as const),
  categoryFilter: oneOf(['all', ...CALL_CATEGORY_OPTIONS] as const),
  projectFilter: projectId,
  agentFilter: text,
  crmEntityTypeFilter: oneOf(['all', 'deal', 'lead', 'contact', 'company'] as const),
  statusFilter: oneOf([
    'all',
    ...RECORD_OUTCOME_FILTERS,
    'delivery_failed',
  ] as const) as FieldReader<StatusFilter>,
  participantFilter: text,
  activityFilter: text,
  dateFrom: day,
  dateTo: day,
  hideShortCalls: flag,
  agentRatingFilter: integer(0, 5),
  agentRatingMode: oneOf(['gte', 'eq', 'lte'] as const),
  qaScoreMin: integer(0, 100),
  qaScoreMax: integer(0, 100),
  sentimentFilter: oneOf(['all', 'positive', 'neutral', 'negative'] as const),
  scriptStepFilter: scriptStep,
};

const FIELDS = Object.keys(READERS) as (keyof PersistedRecordsFilters)[];

/** Whatever was stored, as a complete, valid set — defaults fill every gap. */
export function sanitizePersistedRecordsFilters(raw: unknown): PersistedRecordsFilters {
  const source =
    raw !== null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const next: Record<string, unknown> = {};
  for (const field of FIELDS) {
    const read = READERS[field] as FieldReader<unknown>;
    next[field] = read(source[field], DEFAULT_PERSISTED_RECORDS_FILTERS[field]);
  }
  return next as unknown as PersistedRecordsFilters;
}

export function isDefaultRecordsFilters(values: PersistedRecordsFilters): boolean {
  return FIELDS.every(
    (field) => values[field] === DEFAULT_PERSISTED_RECORDS_FILTERS[field]
  );
}

/** The saved set, or null when there is none (or storage is unavailable). */
export function readSavedRecordsFilters(
  key: string | null
): PersistedRecordsFilters | null {
  if (!key) return null;
  try {
    const stored = window.sessionStorage.getItem(key);
    if (!stored) return null;
    return sanitizePersistedRecordsFilters(JSON.parse(stored));
  } catch {
    return null;
  }
}

/**
 * Remember this set. The default set is not stored — it is the absence of one,
 * which is also how «Сбросить все» clears what was saved.
 */
export function writeSavedRecordsFilters(
  key: string | null,
  values: PersistedRecordsFilters
): void {
  if (!key) return;
  try {
    if (isDefaultRecordsFilters(values)) {
      window.sessionStorage.removeItem(key);
    } else {
      window.sessionStorage.setItem(key, JSON.stringify(values));
    }
  } catch {
    // Storage unavailable or full: the filters still work, they are just not kept.
  }
}
